export type TemplateId = 'formal' | 'compact' | 'thermal';

export interface InvoicePrintSettings {
  templateId: TemplateId;
  primaryColor: string;
  showLogo: boolean;
  showPaymentHistory: boolean;
  showSignatureLine: boolean;
  showNotes: boolean;
  showRUC: boolean;
  footerText: string;
  copyLabel: string;
}

export const DEFAULT_INVOICE_PRINT_SETTINGS: InvoicePrintSettings = {
  templateId: 'formal',
  primaryColor: '#1e40af',
  showLogo: true,
  showPaymentHistory: true,
  showSignatureLine: true,
  showNotes: true,
  showRUC: true,
  footerText: 'Gracias por confiar en nuestros servicios.',
  copyLabel: 'ORIGINAL',
};

export const INVOICE_PRINT_TEMPLATES: { id: TemplateId; label: string; desc: string }[] = [
  { id: 'formal', label: 'Formal A4', desc: 'Diseño corporativo completo con logotipo y bloques de firma' },
  { id: 'compact', label: 'Compacto', desc: 'Todo en una página, fuentes reducidas, sin márgenes extra' },
  { id: 'thermal', label: 'Térmico 80mm', desc: 'Ancho de ticket 80mm para impresoras térmicas' },
];

export const INVOICE_PRINT_COLOR_PRESETS = [
  '#1e40af', '#0f766e', '#7c3aed', '#b91c1c', '#064e3b', '#374151',
];

export const INVOICE_PRINT_STORAGE_KEY = 'invoice_print_settings_v1';

const TEMPLATE_IDS = new Set<string>(['formal', 'compact', 'thermal']);

export function normalizeInvoicePrintSettings(
  raw: unknown
): InvoicePrintSettings {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_INVOICE_PRINT_SETTINGS };
  }
  const src = raw as Record<string, unknown>;
  const templateId = TEMPLATE_IDS.has(String(src.templateId))
    ? (src.templateId as TemplateId)
    : DEFAULT_INVOICE_PRINT_SETTINGS.templateId;

  return {
    templateId,
    primaryColor:
      typeof src.primaryColor === 'string' && src.primaryColor
        ? src.primaryColor
        : DEFAULT_INVOICE_PRINT_SETTINGS.primaryColor,
    showLogo: src.showLogo !== undefined ? Boolean(src.showLogo) : DEFAULT_INVOICE_PRINT_SETTINGS.showLogo,
    showPaymentHistory:
      src.showPaymentHistory !== undefined
        ? Boolean(src.showPaymentHistory)
        : DEFAULT_INVOICE_PRINT_SETTINGS.showPaymentHistory,
    showSignatureLine:
      src.showSignatureLine !== undefined
        ? Boolean(src.showSignatureLine)
        : DEFAULT_INVOICE_PRINT_SETTINGS.showSignatureLine,
    showNotes: src.showNotes !== undefined ? Boolean(src.showNotes) : DEFAULT_INVOICE_PRINT_SETTINGS.showNotes,
    showRUC: src.showRUC !== undefined ? Boolean(src.showRUC) : DEFAULT_INVOICE_PRINT_SETTINGS.showRUC,
    footerText:
      typeof src.footerText === 'string'
        ? src.footerText
        : DEFAULT_INVOICE_PRINT_SETTINGS.footerText,
    copyLabel:
      typeof src.copyLabel === 'string' && src.copyLabel
        ? src.copyLabel
        : DEFAULT_INVOICE_PRINT_SETTINGS.copyLabel,
  };
}

export function loadInvoicePrintSettingsFromLocalStorage(): InvoicePrintSettings | null {
  try {
    const raw = localStorage.getItem(INVOICE_PRINT_STORAGE_KEY);
    if (!raw) return null;
    return normalizeInvoicePrintSettings(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveInvoicePrintSettingsToLocalStorage(settings: InvoicePrintSettings): void {
  try {
    localStorage.setItem(INVOICE_PRINT_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore quota / private mode */
  }
}
